import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { normalizePaginatedResponse } from '@utils/pagination-response.mapper';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
/**
 * Normalizes paginated API responses across backend versions.
 *
 * Why this exists:
 * - Different backend services return pagination metadata in different shapes
 *   (flat fields vs nested `page` object with different key names).
 * - UI services/components should not need environment-specific response parsing.
 *
 * What it does:
 * - Detects known paginated response formats at runtime.
 * - Maps them to one unified structure: `{ content, page: { totalElements, ... } }`.
 * - Also mirrors pagination fields at the top level for backward compatibility.
 *
 * Non-paginated responses are returned untouched.
 */
export class PaginationResponseInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      map(event => {
        if (!(event instanceof HttpResponse)) {
          return event;
        }
        const normalizedBody = normalizePaginatedResponse(event.body);

        if (!normalizedBody) {
          return event;
        }

        return event.clone({ body: normalizedBody });
      })
    );
  }
}
